import axios from "axios";
import {
  safeGetStorageItem,
  safeRemoveStorageItem,
  safeSetStorageItem,
} from "../../../../utility/safeStorage";
import { isAppRuntimeMode } from "./appMode";

export const BUTTON_SIZE = 50;

export const ICON_SIZE = 30;

export const CONFIG = {
  get headers() {
    const token = safeGetStorageItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

export const setAxiosAuthToken = (token = safeGetStorageItem("token")) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }

  return token;
};

const ROOT_ROUTE_NAMES = new Set([
  "login",
  "register",
  "verifycode",
  "map",
  "touch",
  "3v1",
  "unit",
]);

export const DEFAULT_TENANT = "vzs";
export const AUTH_TENANT_STORAGE_KEY = "webdispatcher.authTenant";
export const LAST_SELECTED_TENANT_STORAGE_KEY = "webdispatcher.lastSelectedTenant";

// Tyto instalace používají v API region přímo, bez standardního prefixu ZZS.
// Seznam odpovídá serverovým konfiguracím v aplikaci SmartZOS.
const DIRECT_REGION_TENANTS = new Set([
  "contest",
  "lspcr",
  "sar",
  "smartzos",
  "vzs",
]);

export const normalizeTenant = (value) => {
  if (!value || typeof value !== "string") return null;
  const normalized = value.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
  return normalized || null;
};

export const getBaseTenant = (value) => {
  const normalizedTenant = normalizeTenant(value);
  if (!normalizedTenant) return null;

  // "contest" je název tenantu, nikoli tenant "con" v testovacím prostředí.
  if (normalizedTenant === "contest") return normalizedTenant;

  return normalizedTenant.replace(/(?:test|dev)$/i, "");
};

export function getTenantFromPath(pathname = window.location.pathname) {
  const pathParts = pathname.split('/').filter(Boolean);
  const maybeTenant = pathParts[0];

  if (!maybeTenant) return null;
  if (ROOT_ROUTE_NAMES.has(maybeTenant.toLowerCase())) return null;

  return normalizeTenant(maybeTenant);
}

export function getTenant({ allowStoredFallback = true } = {}) {
  const maybeTenant = getTenantFromPath();

  if (maybeTenant) {
    safeSetStorageItem("tenant", maybeTenant);
    safeSetStorageItem(LAST_SELECTED_TENANT_STORAGE_KEY, maybeTenant);
    return maybeTenant;
  }

  if (!allowStoredFallback) {
    return DEFAULT_TENANT;
  }

  const storedTenant = normalizeTenant(
    safeGetStorageItem(LAST_SELECTED_TENANT_STORAGE_KEY) ||
      safeGetStorageItem("tenant"),
  );
  return storedTenant || DEFAULT_TENANT;
}

const normalizeUrl = (value) => {
  if (!value || typeof value !== "string") return null;
  const normalized = value.trim().replace(/\/+$/, "");
  return normalized || null;
};

const getConfigValue = (key) =>
  window?.appConfig?.[key] || process.env[key] || null;

export function getApiBaseUrl({ allowStoredTenantFallback = true } = {}) {
  const pathTenant = getTenantFromPath();
  const tenant = normalizeTenant(
    pathTenant || (allowStoredTenantFallback ? safeGetStorageItem("tenant") : null),
  ) || DEFAULT_TENANT;
  const normalizedTenant = normalizeTenant(tenant);
  const apiUrlByTenant = window?.appConfig?.REACT_APP_API_URL_BY_TENANT;

  if (normalizedTenant && apiUrlByTenant && typeof apiUrlByTenant === "object") {
    const configuredTenantUrl =
      apiUrlByTenant[tenant] ||
      apiUrlByTenant[normalizedTenant] ||
      apiUrlByTenant[normalizedTenant.toUpperCase()];
    const normalizedConfiguredTenantUrl = normalizeUrl(configuredTenantUrl);
    if (normalizedConfiguredTenantUrl) return normalizedConfiguredTenantUrl;
  }

  if (normalizedTenant) {
    return `https://${normalizedTenant}.smartzos.cz`;
  }

  return normalizeUrl(getConfigValue("REACT_APP_API_URL"));
}

export function configureAxiosBaseUrl() {
  const apiBaseUrl = getApiBaseUrl();

  if (apiBaseUrl) {
    axios.defaults.baseURL = apiBaseUrl;
  }

  setAxiosAuthToken();

  return apiBaseUrl;
}

export const getRegionForTenant = (tenant) => {
  const baseTenant = getBaseTenant(tenant);
  if (!baseTenant) return null;
  const region = baseTenant.toUpperCase();

  return DIRECT_REGION_TENANTS.has(baseTenant)
    ? region
    : `ZZS${region}`;
};

export function getRegion() {
  const maybeTenant = getTenant();
  const region = getRegionForTenant(maybeTenant);

  if (region) safeSetStorageItem("region", region);
  else safeRemoveStorageItem("region", { reason: "no tenant in current url" });
  return region;
}


export const ticksToTime = (ticks) =>
  Math.floor((ticks - 621355968000000000) / 10000000);

export const timeToTicks = (time) => time * 10000000 + 621355968000000000;

export const getDotNetTimestamp = (time) =>
  time * 10000 + 621355968000000000;

export const toFiniteCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const hasUsableEventCoordinates = (lat, lng) => {
  const safeLat = toFiniteCoordinate(lat);
  const safeLng = toFiniteCoordinate(lng);

  if (safeLat === null || safeLng === null) return false;
  if (safeLat === 0 && safeLng === 0) return false;
  if (Math.abs(safeLat) > 90 || Math.abs(safeLng) > 180) return false;

  return true;
};

export const decideBackgroundColor = (color) => {
  if (color === "yellow" || !color) return "black";

  // Funkce pro výpočet jasu barvy (luminance)
  function getLuminance(r, g, b) {
    const a = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }

  // Pokus o získání hodnoty RGB z vstupního řetězce ve formátu "rgb(255, 0, 0)"
  let match = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);

  if (!match) {
    // Pokus o získání hodnoty RGB z vstupního řetězce ve formátu "#RRGGBB"
    match = color.match(/^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/);

    if (!match) {
      return "white"; // Pokud se nepodaří získat hodnoty RGB, vrátíme bílou
    }

    // Převedení šestnáctkových hodnot na desetinné
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);

    // Výpočet jasu barvy
    const luminance = getLuminance(r, g, b);

    // Rozhodování o pozadí na základě jasu
    return luminance > 0.5 ? "black" : "white";
  }

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  // Výpočet jasu barvy
  const luminance = getLuminance(r, g, b);

  // Rozhodování o pozadí na základě jasu
  return luminance > 0.5 ? "black" : "white";
};

export const getAddress = async (coords) => {
  if (isAppRuntimeMode()) {
    return {};
  }

  var URL =
    "https://api.mapy.cz/v0/rgeocode?lon=" +
    coords.longitude +
    "&lat=" +
    coords.latitude +
    "&count=0&apikey=eyJpIjozNjgsImMiOjE2NzQ4MDYxNzJ9.kMYJt8DbrN_lomoBBv4uvgNjvJiT47vSToNf9gDeF3E";
  var result = await axios.get(URL);

  var address = {
    country: null,
    region: null,
    disctrict: null,
    municipal: null,
    ward: null,
    quarter: null,
    street: null,
    address: null,
  };

  var XMLParser = require("react-xml-parser");
  var xml = new XMLParser().parseFromString(result.data); // Assume xmlText contains the example XML
  xml.children.forEach((item) => {
    switch (item.attributes.type) {
      case "coun":
        address.country = item.attributes.name;
        break;
      case "regi":
        address.region = item.attributes.name;
        break;
      case "dist":
        address.disctrict = item.attributes.name;
        break;
      case "muni":
        address.municipal = item.attributes.name;
        break;
      case "ward":
        address.ward = item.attributes.name;
        break;
      case "quar":
        address.quarter = item.attributes.name;
        break;
      case "stre":
        address.street = item.attributes.name;
        break;
      case "addr":
        address.address = item.attributes.name;
        break;
      default:
        break;
    }
  });
  return address;
};

export const removeHTML = (value) => {
  var result = value;

  if (result) {
    result = result.replace(/<br\s*\/?>/gi, "\n");
    const regex = /(<([^>]+)>)/gi;
    result = result.replace(regex, "");
  }

  return result;
};

export const getDM = (value, fixed = 2) => {
  // Required variables
  var result = "";

  value = Math.abs(value); // -40.601203 = 40.601203
  const valueDeg = Math.floor(value); // 40.601203 = 40
  result = valueDeg + "°"; // 40°

  const valueMin = (value - valueDeg) * 60; // 36.07218 = 36
  result += (fixed ? valueMin.toFixed(fixed) : valueMin) + "'"; // 40ş36'

  return result;
};

export const getDMS = (value) => {
  // Required variables
  var result = "";

  value = Math.abs(value); // -40.601203 = 40.601203
  const valueDeg = Math.floor(value); // 40.601203 = 40
  result = valueDeg + "°"; // 40°

  const valueMin = Math.floor((value - valueDeg) * 60); // 36.07218 = 36
  result += valueMin + "'"; // 40ş36'

  const valueSec =
    Math.round((value - valueDeg - valueMin / 60) * 3600 * 1000) / 1000; // 40.601203 = 4.331
  result += Math.round(valueSec) + '"'; // 40ş36'4.331"

  return result;
};

export const formatDistance = (distanceInMeters) => {
  if (
    typeof distanceInMeters !== "number" ||
    isNaN(distanceInMeters) ||
    distanceInMeters < 0
  )
    return "-";

  if (distanceInMeters >= 1000)
    return `${(distanceInMeters / 1000).toFixed(1)} km`;
  else return `${distanceInMeters} m`;
};

export const formatTime = (seconds) => {
  if (typeof seconds !== "number" || isNaN(seconds) || seconds < 0) {
    return "-";
  }

  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (days > 0 && hours > 0 && minutes > 0 && remainingSeconds > 0) {
    return `${days} d ${hours} h ${minutes} min ${remainingSeconds} s`;
  } else if (days > 0 && hours > 0 && minutes > 0) {
    return `${days} d ${hours} h ${minutes} min`;
  } else if (days > 0 && hours > 0 && remainingSeconds > 0) {
    return `${days} d ${hours} h ${remainingSeconds} s`;
  } else if (days > 0 && minutes > 0 && remainingSeconds > 0) {
    return `${days} d ${minutes} min ${remainingSeconds} s`;
  } else if (hours > 0 && minutes > 0 && remainingSeconds > 0) {
    return `${hours} h ${minutes} min ${remainingSeconds} s`;
  } else if (days > 0 && hours > 0) {
    return `${days} d ${hours} h`;
  } else if (days > 0 && minutes > 0) {
    return `${days} d ${minutes} min`;
  } else if (days > 0 && remainingSeconds > 0) {
    return `${days} d ${remainingSeconds} s`;
  } else if (hours > 0 && minutes > 0) {
    return `${hours} h ${minutes} min`;
  } else if (hours > 0 && remainingSeconds > 0) {
    return `${hours} h ${remainingSeconds} s`;
  } else if (minutes > 0 && remainingSeconds > 0) {
    return `${minutes} min ${remainingSeconds} s`;
  } else if (days > 0) {
    return `${days} d`;
  } else if (hours > 0) {
    return `${hours} h`;
  } else if (minutes > 0) {
    return `${minutes} min`;
  } else {
    return `${remainingSeconds} s`;
  }
};

export const mergeArraysById = (array1, array2) => {
  const uniqueObjectsMap = new Map();

  array1.forEach((obj) => uniqueObjectsMap.set(obj.id, obj));
  array2.forEach((obj) => uniqueObjectsMap.set(obj.id, obj));

  const uniqueObjectsArray = Array.from(uniqueObjectsMap.values());

  return uniqueObjectsArray;
};
